import React, { useState, useEffect } from 'react';
import {
  PlusCircle,
  Tags,
  Edit,
  Trash2,
  X,
  Save,
  AlertTriangle,
  Check,
  XCircle,
  Users,
} from 'lucide-react';
import styles from './TipiContattoPage.module.css';
import tipiContattoService, { TipoContatto, TipoContattoRequest } from '@/services/tipiContattoService';
import { coloreTipoContatto } from '@/constants/tipiContatto';
import { ExportDropdown, ExportFormat } from '@/components/common/ExportDropdown';
import { exportToExcel, exportToPdf, ExportColumn } from '@/utils/exports';

const COLORI_SUGGERITI = [
  '#2E7D32', '#1565C0', '#3949AB', '#F57F17', '#00695C',
  '#6A1B9A', '#C62828', '#EF6C00', '#00838F', '#558B2F',
];

export const TipiContattoPage: React.FC = () => {
  const [tipiContatto, setTipiContatto] = useState<TipoContatto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingTipo, setEditingTipo] = useState<TipoContatto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TipoContatto | null>(null);

  const [formData, setFormData] = useState({
    codice: '',
    nome: '',
    descrizione: '',
    colore: COLORI_SUGGERITI[0],
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState<{ nome?: boolean; codice?: string }>({});
  const [formLoading, setFormLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    loadTipiContatto();
  }, []);

  const loadTipiContatto = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tipiContattoService.getTipiContatto();
      setTipiContatto(data);
    } catch (err) {
      console.error('Error loading tipi contatto:', err);
      setError('Errore nel caricamento dei tipi contatto');
    } finally {
      setLoading(false);
    }
  };

  const openNewModal = () => {
    setEditingTipo(null);
    setFormData({
      codice: '',
      nome: '',
      descrizione: '',
      colore: COLORI_SUGGERITI[tipiContatto.length % COLORI_SUGGERITI.length],
      isActive: true,
    });
    setFormErrors({});
    setShowFormModal(true);
  };

  const openEditModal = (tipo: TipoContatto) => {
    setEditingTipo(tipo);
    setFormData({
      codice: tipo.codice,
      nome: tipo.nome,
      descrizione: tipo.descrizione || '',
      colore: tipo.colore || coloreTipoContatto(tipo.codice),
      isActive: tipo.isActive,
    });
    setFormErrors({});
    setShowFormModal(true);
  };

  const openDeleteModal = (tipo: TipoContatto) => {
    setDeleteTarget(tipo);
    setShowDeleteModal(true);
  };

  const handleSave = async () => {
    const errors: { nome?: boolean } = {};
    if (!formData.nome.trim()) errors.nome = true;
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      setFormLoading(true);
      const requestData: TipoContattoRequest = {
        codice: formData.codice || undefined,
        nome: formData.nome,
        descrizione: formData.descrizione || undefined,
        colore: formData.colore || undefined,
        isActive: formData.isActive,
      };

      if (editingTipo) {
        await tipiContattoService.updateTipoContatto(editingTipo.id, requestData);
      } else {
        await tipiContattoService.createTipoContatto(requestData);
      }

      await loadTipiContatto();
      setFormErrors({});
      setShowFormModal(false);
    } catch (err: unknown) {
      console.error('Error saving tipo contatto:', err);
      const axiosError = err as { response?: { status?: number; data?: { message?: string } } };
      if (axiosError?.response?.status === 400) {
        const message = axiosError.response.data?.message || '';
        if (message.toLowerCase().includes('codice')) {
          setFormErrors((prev) => ({ ...prev, codice: message }));
          return;
        }
      }
      setError('Errore nel salvataggio del tipo contatto');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setLoading(true);
      await tipiContattoService.deleteTipoContatto(deleteTarget.id);
      await loadTipiContatto();
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (err: unknown) {
      console.error('Error deleting tipo contatto:', err);
      const axiosError = err as { response?: { status?: number; data?: { message?: string } } };
      setError(axiosError?.response?.data?.message || 'Errore nell\'eliminazione del tipo contatto');
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors((prev) => ({ ...prev, [field]: field === 'codice' ? undefined : false }));
    }
  };

  const handleExport = async (format: ExportFormat) => {
    if (tipiContatto.length === 0) {
      alert('Nessun dato da esportare');
      return;
    }

    setExportLoading(true);
    try {
      const columns: ExportColumn[] = [
        { field: 'codice', header: 'Codice', type: 'text', width: 18 },
        { field: 'nome', header: 'Nome', type: 'text', width: 25 },
        { field: 'descrizione', header: 'Descrizione', type: 'text', width: 35 },
        { field: 'numeroContatti', header: 'Contatti', type: 'text', width: 10 },
        { field: 'isActiveDisplay', header: 'Attivo', type: 'text', width: 10 },
      ];

      const exportData = tipiContatto.map((t) => ({
        codice: t.codice,
        nome: t.nome,
        descrizione: t.descrizione || '',
        numeroContatti: String(t.numeroContatti ?? 0),
        isActiveDisplay: t.isActive ? 'Sì' : 'No',
      }));

      const config = {
        title: 'Tipi Contatto',
        filename: 'tipi_contatto',
        columns,
        orientation: 'portrait' as const,
      };

      if (format === 'excel') {
        await exportToExcel(config, exportData);
      } else {
        await exportToPdf(config, exportData);
      }
    } catch (err) {
      console.error('Error exporting tipi contatto:', err);
      setError('Errore durante l\'esportazione');
    } finally {
      setExportLoading(false);
    }
  };

  const totalTipi = tipiContatto.length;
  const attivi = tipiContatto.filter((t) => t.isActive).length;
  const inattivi = tipiContatto.filter((t) => !t.isActive).length;

  return (
    <div className={styles.container}>
      <main>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>
            <Tags size={32} style={{ verticalAlign: 'middle', marginRight: '12px' }} />
            Tipi Contatto
          </h1>
          <div className={styles.pageActions}>
            <ExportDropdown
              onExport={handleExport}
              disabled={tipiContatto.length === 0}
              loading={exportLoading}
            />
            <button className={styles.btnPrimary} onClick={openNewModal} disabled={loading}>
              <PlusCircle size={18} />
              Nuovo Tipo
            </button>
          </div>
        </div>

        {error && (
          <div className={styles.errorBanner}>
            <AlertTriangle size={20} />
            {error}
            <button onClick={() => setError(null)} className={styles.btnIcon}>
              <X size={16} />
            </button>
          </div>
        )}

        <div className={styles.statsBar}>
          <div className={styles.statItem}>
            <Tags size={20} style={{ color: 'var(--primary-main)' }} />
            <span className={styles.statNumber}>{loading ? '...' : totalTipi}</span>
            <span className={styles.statText}>Totale</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <Check size={20} style={{ color: 'var(--success-main)' }} />
            <span className={styles.statNumber}>{loading ? '...' : attivi}</span>
            <span className={styles.statText}>Attivi</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <XCircle size={20} style={{ color: 'var(--text-secondary)' }} />
            <span className={styles.statNumber}>{loading ? '...' : inattivi}</span>
            <span className={styles.statText}>Inattivi</span>
          </div>
        </div>

        <div className={styles.card}>
          {loading && <div className={styles.loadingState}>Caricamento tipi contatto...</div>}

          {!loading && tipiContatto.length === 0 && (
            <div className={styles.emptyState}>
              <Tags size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
              <div>Nessun tipo contatto trovato</div>
              <button className={styles.btnPrimary} onClick={openNewModal} style={{ marginTop: '16px' }}>
                <PlusCircle size={18} />
                Crea Primo Tipo
              </button>
            </div>
          )}

          {!loading && tipiContatto.length > 0 && (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Codice</th>
                    <th>Nome</th>
                    <th>Descrizione</th>
                    <th>Contatti</th>
                    <th>Stato</th>
                    <th className={styles.textRight}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {tipiContatto.map((tipo) => (
                    <tr key={tipo.id}>
                      <td>
                        <span className={styles.codice}>{tipo.codice}</span>
                      </td>
                      <td>
                        <div className={styles.nomeCell}>
                          <span
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              display: 'inline-block',
                              background: coloreTipoContatto(tipo.codice, tipo.colore),
                            }}
                          />
                          <span>{tipo.nome}</span>
                        </div>
                      </td>
                      <td className={styles.descrizione}>{tipo.descrizione || '-'}</td>
                      <td>
                        <span className={styles.nomeCell}>
                          <Users size={14} style={{ color: 'var(--text-secondary)' }} />
                          {tipo.numeroContatti ?? 0}
                        </span>
                      </td>
                      <td>
                        <span className={`${styles.badge} ${tipo.isActive ? styles.badgeSuccess : styles.badgeInactive}`}>
                          {tipo.isActive ? 'Attivo' : 'Inattivo'}
                        </span>
                      </td>
                      <td className={styles.textRight}>
                        <div className={styles.rowActions}>
                          <button className={styles.btnIcon} onClick={() => openEditModal(tipo)} title="Modifica">
                            <Edit size={16} />
                          </button>
                          <button className={styles.btnIcon} onClick={() => openDeleteModal(tipo)} title="Elimina">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {showFormModal && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowFormModal(false); }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {editingTipo ? <Edit size={24} /> : <PlusCircle size={24} />}
                <span style={{ marginLeft: '8px' }}>
                  {editingTipo ? 'Modifica Tipo Contatto' : 'Nuovo Tipo Contatto'}
                </span>
              </h3>
              <button className={styles.modalClose} onClick={() => setShowFormModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Nome *</label>
                <input
                  type="text"
                  className={`${styles.formControl} ${formErrors.nome ? styles.error : ''}`}
                  placeholder="Es: Rivenditore, Fornitore, Cliente estero..."
                  value={formData.nome}
                  onChange={(e) => handleFormChange('nome', e.target.value)}
                />
                {formErrors.nome && <span className={styles.errorText}>Campo obbligatorio</span>}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Codice</label>
                <input
                  type="text"
                  className={`${styles.formControl} ${formErrors.codice ? styles.error : ''}`}
                  placeholder="Lascia vuoto per generarlo dal nome"
                  value={formData.codice}
                  onChange={(e) => handleFormChange('codice', e.target.value)}
                />
                {formErrors.codice ? (
                  <span className={styles.errorText}>{formErrors.codice}</span>
                ) : (
                  <span className={styles.helpText}>
                    Se vuoto viene ricavato dal nome (es. &quot;Cliente estero&quot; → CLIENTE_ESTERO)
                  </span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Colore</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {COLORI_SUGGERITI.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => handleFormChange('colore', c)}
                      title={c}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: c,
                        cursor: 'pointer',
                        border: formData.colore === c ? '3px solid var(--text-primary)' : '2px solid transparent',
                      }}
                    />
                  ))}
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Descrizione</label>
                <textarea
                  className={styles.formControl}
                  rows={3}
                  placeholder="Descrizione opzionale..."
                  value={formData.descrizione}
                  onChange={(e) => handleFormChange('descrizione', e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.checkboxWrapper}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={formData.isActive}
                    onChange={(e) => handleFormChange('isActive', e.target.checked)}
                  />
                  <span>Tipo contatto attivo</span>
                </label>
                <span className={styles.helpText}>
                  Un tipo disattivato resta sui contatti già classificati ma non è più proponibile
                </span>
              </div>
              <div className={styles.helpText} style={{ marginTop: '16px' }}>
                * Campi obbligatori
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.btnSecondary} onClick={() => setShowFormModal(false)}>
                Annulla
              </button>
              <button className={styles.btnPrimary} onClick={handleSave} disabled={formLoading}>
                <Save size={16} />
                {formLoading ? 'Salvataggio...' : editingTipo ? 'Salva Modifiche' : 'Crea'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && deleteTarget && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowDeleteModal(false); }}>
          <div className={styles.modalConfirm} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>
              <AlertTriangle size={48} />
            </div>
            <h3 className={styles.confirmTitle}>Conferma Eliminazione</h3>
            <p className={styles.confirmText}>
              Sei sicuro di voler eliminare il tipo contatto <strong>&quot;{deleteTarget.nome}&quot;</strong>?
              {(deleteTarget.numeroContatti ?? 0) > 0 && (
                <>
                  <br />
                  È usato da {deleteTarget.numeroContatti} contatti: riassegnali prima di eliminarlo,
                  oppure disattivalo.
                </>
              )}
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.btnSecondary} onClick={() => setShowDeleteModal(false)}>
                Annulla
              </button>
              <button className={styles.btnDanger} onClick={handleDeleteConfirm}>
                <Trash2 size={16} />
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
